import React, { useState } from 'react';
import { FiCheck, FiClock, FiMail, FiMapPin, FiPhone, FiSend } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AnnouncementBar from './AnnouncementBar';
import Header from './Header';
import Footer from './Footer';
import supabase from '../supabase/supabase';

const services = [
  'Mobile Notary',
  'Remote Online Notary',
  'General Notarizations',
  'Apostille Services',
  'I-9 Employment Verification',
  'Business & Corporate Documents',
  'Medical & Healthcare',
  'Family & Estate Planning',
  'Vehicle Documents'
];

const initialForm = {
  full_name: '',
  phone_number: '',
  email_address: '',
  service_needed: '',
  message: ''
};

const ContactPage = () => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));

    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    const { error: invokeError } = await supabase.functions.invoke(
      'send-contact-form',
      { body: formData }
    );

    if (invokeError) {
      setError('Something went wrong, please call or text us instead.');
      setIsSubmitting(false);
      return;
    }

    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col selection:bg-brass selection:text-cream">
      <AnnouncementBar />
      <Header />
      <main className="flex-grow">
        <section className="px-6 pt-16 pb-20 md:pt-24 md:pb-28 bg-cream-dark">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <span className="text-brass text-xs font-bold tracking-[0.2em] uppercase mb-5 block">
                Get In Touch
              </span>
              <h1 className="font-fraunces text-5xl md:text-6xl text-navy leading-tight mb-6">
                Let&apos;s get your document handled.
              </h1>
              <p className="text-lg md:text-xl text-navy/70 max-w-2xl leading-relaxed">
                Same-day appointments available. Call for the fastest response,
                or fill out the form and we&apos;ll be in touch shortly.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-16">
              <ContactInformation />
              <MessageForm
                formData={formData}
                isSubmitting={isSubmitting}
                isSubmitted={isSubmitted}
                error={error}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const ContactInformation = () => {
  return (
    <div className="space-y-6">
      <h2 className="font-fraunces text-3xl text-navy mb-8">
        Contact Information
      </h2>

      <div className="bg-cream rounded-2xl border border-line p-6">
        <div className="flex gap-4">
          <SafeIcon icon={FiPhone} size={22} className="text-brass mt-1 shrink-0" />
          <div>
            <h3 className="font-bold text-navy mb-1">Phone</h3>
            <a
              href="tel:2405713117"
              className="text-lg font-semibold text-navy hover:text-brass transition-colors"
            >
              (240) 571-3117
            </a>
            <p className="text-sm text-navy/60 mt-1">Fastest response, call or text</p>
          </div>
        </div>

        <div className="flex gap-4 mt-6 pt-6 border-t border-line">
          <SafeIcon icon={FiMail} size={22} className="text-brass mt-1 shrink-0" />
          <div>
            <h3 className="font-bold text-navy mb-1">Email</h3>
            <a
              href="mailto:carlos@prontonotarymd.com"
              className="text-navy/70 hover:text-brass transition-colors"
            >
              carlos@prontonotarymd.com
            </a>
          </div>
        </div>

        <div className="flex gap-4 mt-6 pt-6 border-t border-line">
          <SafeIcon icon={FiMapPin} size={22} className="text-brass mt-1 shrink-0" />
          <div>
            <h3 className="font-bold text-navy mb-1">Location</h3>
            <p className="text-navy/70">
              Mobile, we come to you.{' '}
              <a
                href="/service-areas"
                className="font-semibold text-navy underline decoration-brass underline-offset-4 hover:text-brass transition-colors"
              >
                Service areas
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="bg-navy text-cream rounded-2xl p-6">
        <div className="flex gap-4">
          <SafeIcon icon={FiClock} size={22} className="text-brass mt-1 shrink-0" />
          <div>
            <h3 className="font-bold mb-2">Hours</h3>
            <p className="text-cream/75">Evenings and weekends</p>
          </div>
        </div>
      </div>

      <div className="bg-cream rounded-2xl border border-line p-6">
        <h3 className="font-fraunces text-2xl text-navy mb-5">What to Prepare</h3>
        <ul className="space-y-4">
          {[
            'Valid government-issued photo ID',
            'Document ready but unsigned',
            'Your preferred location and time',
            'Any special instructions'
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-navy/75">
              <SafeIcon icon={FiCheck} size={18} className="text-brass mt-0.5 shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const MessageForm = ({
  formData,
  isSubmitting,
  isSubmitted,
  error,
  onChange,
  onSubmit
}) => {
  if (isSubmitted) {
    return (
      <div className="bg-cream rounded-2xl border border-line p-8 md:p-10 flex items-center justify-center min-h-[420px]">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-brass/15 text-brass flex items-center justify-center mx-auto mb-5">
            <SafeIcon icon={FiCheck} size={28} />
          </div>
          <h2 className="font-fraunces text-3xl text-navy mb-3">
            Thanks, we&apos;ll be in touch shortly.
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream rounded-2xl border border-line p-8 md:p-10 shadow-sm">
      <h2 className="font-fraunces text-3xl text-navy mb-7">Send a Message</h2>

      {error && (
        <p role="alert" className="mb-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <form className="space-y-5" onSubmit={onSubmit}>
        <Field
          id="full_name"
          name="full_name"
          label="Full Name"
          value={formData.full_name}
          onChange={onChange}
          required
        />
        <Field
          id="phone_number"
          name="phone_number"
          label="Phone Number"
          type="tel"
          value={formData.phone_number}
          onChange={onChange}
        />
        <Field
          id="email_address"
          name="email_address"
          label="Email Address"
          type="email"
          value={formData.email_address}
          onChange={onChange}
          required
        />

        <div>
          <label htmlFor="service_needed" className="block text-sm font-semibold text-navy mb-2">
            Service Needed
          </label>
          <select
            id="service_needed"
            name="service_needed"
            value={formData.service_needed}
            onChange={onChange}
            className="w-full bg-transparent border border-line rounded-lg px-4 py-3 text-navy focus:border-brass transition-colors appearance-none outline-none"
          >
            <option value="">Select a service...</option>
            {services.map((service) => (
              <option value={service} key={service}>{service}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-navy mb-2">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows="6"
            value={formData.message}
            onChange={onChange}
            required
            placeholder="Please describe your document type, location, and preferred appointment time..."
            className="w-full bg-transparent border border-line rounded-lg px-4 py-3 placeholder:text-navy/40 focus:border-brass transition-colors resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-navy text-cream font-bold py-4 rounded-full hover:bg-brass transition-colors mt-2 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
        >
          <SafeIcon icon={FiSend} size={18} />
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </button>
      </form>
    </div>
  );
};

const Field = ({ id, name, label, type = 'text', value, onChange, required = false }) => {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-navy mb-2">
        {label}{required && <span className="text-brass"> *</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border border-line rounded-lg px-4 py-3 placeholder:text-navy/40 focus:border-brass transition-colors"
      />
    </div>
  );
};

export default ContactPage;